import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { SchedulerHeader } from './scheduler/SchedulerHeader';
import { TripIntentPanel } from './scheduler/TripIntentPanel';
import { SchedulerGrid } from './scheduler/SchedulerGrid';
import { DecisionSupportPanel } from './scheduler/DecisionSupportPanel';
import { Vehicle, Booking, TripIntent, AvailabilityWindow, ZoomLevel, ViewMode, MaintenancePeriod, VehicleType, BookingStatus } from './scheduler/types';
import { generateMockVehicles, generateMockBookings, generateMockMaintenancePeriods } from './scheduler/mockData';
import { calculateVehicleFeasibility } from './scheduler/feasibilityEngine';
import { api } from '../../services/api';

// Set to true to use mock data, false to use real API
// const USE_MOCK_DATA = true;

export function VehicleScheduler() {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [zoomLevel, setZoomLevel] = useState<ZoomLevel>('4h'); // Default: 4 hours zoom level
  const [searchQuery, setSearchQuery] = useState('');
  const [availabilityWindow, setAvailabilityWindow] = useState<AvailabilityWindow | null>(null);
  const [selectedVehicle, setSelectedVehicle] = useState<Vehicle | null>(null);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [maintenancePeriods, setMaintenancePeriods] = useState<MaintenancePeriod[]>([]);
  const [scrollToTime, setScrollToTime] = useState<Date | undefined>();
  const [isSupportPanelOpen, setIsSupportPanelOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load data from API or mock
  useEffect(() => {
    async function loadData() {
      try {
        setLoading(true);
        setError(null);

        // if (USE_MOCK_DATA) {
        // Use mock data

        const [carsResponse, bookingResponse] = await Promise.all([
          api.getCars(),
          api.fetchBookings()
        ]);
        const carsData = Array.isArray(carsResponse) ? carsResponse : (carsResponse || []);
        const vehiclesData: Vehicle[] = carsData.map((car: any) => {
          // Map car model/fuel type to VehicleType
          let vehicleType: VehicleType = 'Maruti Suzuki Dzire'; // default

          const model = car.carType?.model?.toLowerCase() || '';
          // const makeCompany = car.carType?.makeCompany?.toLowerCase() || '';

          // Match to exact enum values
          if (model.includes('dzire')) {
            vehicleType = 'Maruti Suzuki Dzire';
          } else if (model.includes('rumion')) {
            vehicleType = 'Toyota Rumion S';
          } else if (model.includes('carens')) {
            vehicleType = 'Kia Carens Premium';
          } else if (model.includes('crysta')) {
            vehicleType = 'Toyota Crysta GX 7 STR';
          }

          return {
            id: car.id?.toString() || '',
            vehicleName: `${car.carType?.makeCompany || ''} ${car.carType?.model || ''}`.trim() || 'Unknown Vehicle',
            vehicleNumber: car.plateNumber || 'N/A',
            vehicleType: vehicleType,
            currentCity: 'Pune' as any,
            currentLocation: 'Unknown',
            driverName: car.driverName || 'Unassigned'
          };
        });
        const tripsData = (bookingResponse as any)?.data || [];
        const bookingsData: Booking[] = tripsData.map((trip: any) => {
          // let status: BookingStatus = 'confirmed';
          // const apiStatus = trip.status?.toUpperCase();

          // if (apiStatus === 'CONFIRMED') {
          //   status = 'confirmed';
          // } else if (apiStatus === 'IN PROGRESS') {
          //   status = 'inprogress';
          // }
          return {
            id: trip.id?.toString(),
            vehicleId: trip.vehicleId,
            startTime: new Date(trip.startTime),
            endTime: new Date(trip.endTime),
            status: (trip.status?.toLowerCase()) as any,
            pickupCity: trip.pickupCity,
            dropCity: trip.dropCity,
            pickupLocation: trip.pickupLocation,
            dropLocation: trip.dropLocation,
            bookingId: trip.bookingId,
            description: trip.description,
            // customerName: trip.customerName,
          };
        });
        const mockVehicles = generateMockVehicles();
        // const mockBookings = generateMockBookings();
        setVehicles(vehiclesData);
        setBookings(bookingsData);
        setMaintenancePeriods(generateMockMaintenancePeriods(mockVehicles));
        // }
      } catch (err: any) {
        setError(err.message);
        console.error('Failed to load data:', err);
      } finally {
        setLoading(false);
      }
    }

    loadData();

    // Optional: Refresh data every 60 seconds (only for real API)
    // if (!USE_MOCK_DATA) {
    //   // const interval = setInterval(loadData, 60000);
    //   const interval = setInterval(loadData, 3 * 60 * 1000);
    //   return () => clearInterval(interval);
    // }
  }, [selectedDate]);

  // Ensure default zoom level is set on mount
  useEffect(() => {
    setZoomLevel('4h');
  }, []);

  // Calculate optimal zoom level based on trip duration
  const calculateOptimalZoom = useCallback((durationMinutes: number): ZoomLevel => {
    const durationHours = durationMinutes / 60;

    // Assume viewport can show approximately 12-16 hours comfortably at different zoom levels
    // Add 20% buffer for comfortable viewing
    const bufferedDuration = durationHours * 1.4;

    // if (bufferedDuration <= 16) {
    //   return '2h'; // Most detailed, shows ~12 hours
    // } else if (bufferedDuration <= 24) {
    //   return '2h'; // Medium detail, shows ~24 hours
    // } else if (bufferedDuration <= 48) {
    //   return '4h'; // Less detail, shows ~48 hours
    // } else {
    //   return '1d'; // Least detail, shows multiple days
    // }
    if (bufferedDuration <= 24) {
      return '2h'; // Most detailed, shows ~24 hours
    } else if (bufferedDuration <= 48) {
      return '4h'; // Medium detail, shows ~48 hours
    } else if (bufferedDuration <= 96) {
      return '8h'; // Less detail, shows ~96 hours
    } else {
      return '1d'; // Least detail, shows multiple days
    }
  }, []);

  const handleShowAvailability = useCallback((tripIntent: TripIntent) => {
    const pickupTime = new Date(tripIntent.pickupDateTime);
    const endTime = new Date(pickupTime.getTime() + tripIntent.estimatedDuration * 60000);

    setAvailabilityWindow({
      startTime: pickupTime,
      endTime: endTime,
      tripIntent: tripIntent
    });

    // Automatically adjust zoom level for optimal viewing
    const optimalZoom = calculateOptimalZoom(tripIntent.estimatedDuration);
    setZoomLevel(optimalZoom);

    // Scroll to pickup time
    setScrollToTime(pickupTime);
    setIsSupportPanelOpen(true);
  }, [calculateOptimalZoom]);

  const handleTripIntentChange = useCallback((tripIntent: TripIntent) => {
    // Real-time update: Update availability window as fields change
    const pickupTime = new Date(tripIntent.pickupDateTime);
    const endTime = new Date(pickupTime.getTime() + tripIntent.estimatedDuration * 60000);

    setAvailabilityWindow(prevWindow => {
      const newWindow = {
        startTime: pickupTime,
        endTime: endTime,
        tripIntent: tripIntent
      };

      // If window is already active and duration changed significantly, adjust zoom
      if (prevWindow) {
        const oldDuration = (prevWindow.endTime.getTime() - prevWindow.startTime.getTime()) / 60000;
        const newDuration = tripIntent.estimatedDuration;
        const durationChange = Math.abs(newDuration - oldDuration) / oldDuration;

        // If duration changed by more than 50%, recalculate optimal zoom
        if (durationChange > 0.5) {
          const optimalZoom = calculateOptimalZoom(tripIntent.estimatedDuration);
          setZoomLevel(optimalZoom);
        }
      }

      return newWindow;
    });

    // Auto-scroll to bring the vertical bars into view
    setScrollToTime(pickupTime);
  }, [calculateOptimalZoom]);

  const handleClearAvailability = () => {
    setAvailabilityWindow(null);
    setSelectedVehicle(null);
    setIsSupportPanelOpen(false);
  };

  const handleVehicleSelect = (vehicle: Vehicle) => {
    setSelectedVehicle(vehicle);
    setIsSupportPanelOpen(true);
  };

  // const handleJumpToNow = () => {
  //   setScrollToTime(new Date());
  // };

  // const handleJumpToPickup = () => {
  //   if (availabilityWindow) {
  //     setScrollToTime(availabilityWindow.startTime);
  //   }
  // };

  // const handleNavigate = (hours: number) => {
  //   const newDate = new Date(selectedDate);
  //   newDate.setHours(newDate.getHours() + hours);
  //   setSelectedDate(newDate);
  //   setScrollToTime(newDate);
  // };

  const filteredVehicles = vehicles.filter(v =>
    searchQuery === '' ||
    v.vehicleNumber.toLowerCase().includes(searchQuery.toLowerCase())
    // v.driverName.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Calculate feasibility for selected vehicle
  // const selectedVehicleFeasibility = selectedVehicle && availabilityWindow
  //   ? calculateVehicleFeasibility(
  //     selectedVehicle,
  //     bookings.filter(b => b.vehicleId === selectedVehicle.id),
  //     maintenancePeriods.filter(m => m.vehicleId === selectedVehicle.id),
  //     availabilityWindow
  //   )
  //   : null;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: 'grey.50', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2, color: 'black' }}>Loading scheduler...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: 'grey.50', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="h6" sx={{ color: 'error.main' }}>Error: {error}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: 'grey.50' }}>
      <SchedulerHeader
        selectedDate={selectedDate}
        onDateChange={setSelectedDate}
        zoomLevel={zoomLevel}
        onZoomLevelChange={setZoomLevel}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        onTripIntentChange={handleTripIntentChange}
        onShowAvailability={handleShowAvailability}
        onClearAvailability={handleClearAvailability}
        hasActiveWindow={availabilityWindow !== null}
      />

      <SchedulerGrid
        vehicles={filteredVehicles}
        bookings={bookings}
        maintenancePeriods={maintenancePeriods}
        selectedDate={selectedDate}
        zoomLevel={zoomLevel}
        availabilityWindow={availabilityWindow}
        selectedVehicle={selectedVehicle}
        onVehicleSelect={handleVehicleSelect}
        scrollToTime={scrollToTime}
      />
    </Box>
  );
}